import {
  composeLoggers,
  composeRootErrorOptions,
  initWebTelemetry,
  type TelemetryLogger,
} from '@trinity/observability-web';
import { createSentryLogger, createSentryRootOptions } from '@trinity/react-utils/sentry';
import { config } from '@/config';

/**
 * Where browsers send traces: the chat API relays them to the deployment's collector, so the app never talks to a
 * collector directly. The relay lives at the API's origin under `/v1`.
 */
function apiOrigin(): string {
  return new URL(config().BRAND_INSIGHTS_API_URL).origin;
}

function sentryOrigin(): string | undefined {
  const dsn = config().SENTRY_DSN;
  return dsn ? new URL(dsn).origin : undefined;
}

export const telemetry = initWebTelemetry({
  enabled: config().OTEL_ENABLED,
  serviceName: 'chat',
  serviceVersion: config().SENTRY_RELEASE,
  environment: config().APP_ENVIRONMENT,
  tracesEndpoint: `${apiOrigin()}/v1/telemetry/traces`,
  sampleRatio: config().OTEL_TRACES_SAMPLE_RATE,
  // Trace headers go to our API only; requests to anyone else are timed but carry no headers.
  propagateTraceHeaderTo: [apiOrigin()],
  ignoreUrls: sentryOrigin() ? [sentryOrigin() as string] : [],
});

/** Sentry as an optional extra sink: its calls do nothing unless Sentry was initialized (SENTRY_ENABLED). */
const sentryLogger: TelemetryLogger = createSentryLogger();

/**
 * The app's error reporter. OpenTelemetry is the primary sink; Sentry (when enabled) receives the same reports.
 * Also what `@trinity/chat-components` reports through, via `TelemetryLoggerProvider`.
 */
export const logger: TelemetryLogger = config().SENTRY_ENABLED
  ? composeLoggers(telemetry.logger, sentryLogger)
  : telemetry.logger;

/**
 * `createRoot` options so React's own error callbacks (uncaught, caught by a boundary, recoverable) are reported.
 * OpenTelemetry gets all three. Sentry, when enabled, keeps its own `reactErrorHandler` for uncaught and recoverable
 * errors, as before, so reporting to it does not depend on the OpenTelemetry flag; errors caught by a boundary are
 * already captured by the Sentry `ErrorBoundary`.
 */
export const rootErrorOptions = composeRootErrorOptions(
  telemetry.rootErrorOptions,
  config().SENTRY_ENABLED ? createSentryRootOptions() : {}
);
